import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../core/hooks/use-language';
import { useSpecialists } from '../../core/hooks/use-specialists';
import { useTranslation } from '../../core/hooks/use-translation';
import type { TranslationKey } from '../../core/i18n/translation';
import type { OfficeCity } from '../../core/models/office-city';
import { citiesWithSpecialists } from './specialist-cities';
import { SpecialistDetails } from './specialist-details';

const AUTOPLAY_MS = 3200;

const CITY_LABEL: Record<OfficeCity, TranslationKey> = {
    Astana: 'site.city.astana',
    Almaty: 'site.city.almaty'
};

/**
 * The consultants, as a horizontally scrolling row with a city toggle above it.
 *
 * **They are staff data now, not a build-time constant.** Adding a specialist is an administrator
 * switching a card on in WebAdmin rather than a deployment — which also means this section renders
 * nothing at all until somebody does, and rendering nothing is the deliberate answer: an empty
 * carousel with two tabs and a pair of arrows is worse than no section.
 *
 * **Every card of both cities is in the DOM and the other city's are `hidden`**, which is what the
 * live site does and is worth keeping: switching city is then a paint rather than a fetch, and the
 * photographs of the city the visitor did not pick are still preloaded by the time they change
 * their mind.
 *
 * The scroll position, not a transform, is what moves: the row is a real scroller, so a touch drag
 * works without any code and the arrows are `scrollBy` on top of it.
 *
 * **The cards are in the served page, not fetched after it.** The build renders them from the list
 * it read, and nginx inlines the list the API rewrites on every change — see `site-data-store`. Do
 * not solve staleness by putting the list back in the bundle.
 */
export function SpecialistsSection() {
    const { t } = useTranslation();
    const language = useLanguage();
    const viewport = useRef<HTMLDivElement>(null);
    const { specialists } = useSpecialists();
    const cities = useMemo(() => citiesWithSpecialists(specialists), [specialists]);
    const [chosenCity, setChosenCity] = useState<OfficeCity | null>(null);
    const [paused, setPaused] = useState(false);
    const [detailsId, setDetailsId] = useState<string | null>(null);
    const details = specialists.find((specialist) => specialist.id === detailsId);

    // The first office that has anybody in it, until the visitor picks one. Derived rather than
    // defaulted to Astana, so a roster with only Almaty in it does not open on an empty row.
    const city = chosenCity !== null && cities.includes(chosenCity) ? chosenCity : (cities[0] ?? null);

    const step = useCallback(() => {
        const element = viewport.current;

        if (element === null) {
            return 0;
        }

        const card = element.querySelector<HTMLElement>('.spec-card:not([hidden])');

        if (card === null) {
            return element.clientWidth;
        }

        const track = card.parentElement;
        const gap = track === null ? 0 : parseFloat(getComputedStyle(track).columnGap) || 0;

        return card.offsetWidth + gap;
    }, []);

    const goNext = useCallback(() => {
        const element = viewport.current;

        if (element === null) {
            return;
        }

        // Past the last card, wrap to the start rather than sitting at the end doing nothing.
        if (element.scrollLeft + element.clientWidth >= element.scrollWidth - 4) {
            element.scrollTo({ left: 0, behavior: 'smooth' });
        } else {
            element.scrollBy({ left: step(), behavior: 'smooth' });
        }
    }, [step]);

    const goPrevious = useCallback(() => {
        const element = viewport.current;

        if (element === null) {
            return;
        }

        if (element.scrollLeft <= 4) {
            element.scrollTo({ left: element.scrollWidth, behavior: 'smooth' });
        } else {
            element.scrollBy({ left: -step(), behavior: 'smooth' });
        }
    }, [step]);

    useEffect(() => {
        if (paused || detailsId !== null || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            return;
        }

        const timer = window.setInterval(goNext, AUTOPLAY_MS);

        return () => window.clearInterval(timer);
    }, [paused, detailsId, goNext, city]);

    useEffect(() => {
        const onVisibility = () => setPaused(document.hidden);

        document.addEventListener('visibilitychange', onVisibility);

        return () => document.removeEventListener('visibilitychange', onVisibility);
    }, []);

    const selectCity = (next: OfficeCity) => {
        setChosenCity(next);
        viewport.current?.scrollTo({ left: 0 });
    };

    // Nothing to show and nothing to say about it. A heading over an empty row would read as a
    // company with no consultants rather than as a page still filling up.
    //
    // The test is on the cities rather than on the cards, and it is the stricter of the two: cards
    // whose city this bundle does not know would leave every one of them `hidden` behind a toggle
    // with no tabs in it, which is the same empty row arrived at from a vocabulary that drifted.
    if (cities.length === 0) {
        return null;
    }

    return (
        <section className="section spec-section" id="specialists">
            <div className="container">
                <div className="section-header centered">
                    <span className="section-label">{t('site.spec.label')}</span>
                    <h2 className="section-title">{t('site.spec.title')}</h2>
                    <p className="section-subtitle">{t('site.spec.subtitle')}</p>
                </div>

                {/* One office is not a choice, and a tablist with a single tab in it says otherwise. */}
                {cities.length > 1 && (
                    <div className="spec-toggle" role="tablist" aria-label={t('site.specialists.city')}>
                        {cities.map((option) => (
                            <button
                                key={option}
                                type="button"
                                role="tab"
                                className={option === city ? 'spec-toggle-btn is-active' : 'spec-toggle-btn'}
                                aria-selected={option === city}
                                onClick={() => selectCity(option)}
                            >
                                {t(CITY_LABEL[option])}
                            </button>
                        ))}
                    </div>
                )}

                <div className="spec-carousel">
                    <div
                        className="spec-viewport"
                        ref={viewport}
                        onMouseEnter={() => setPaused(true)}
                        onMouseLeave={() => setPaused(false)}
                    >
                        <div className="spec-track">
                            {specialists.map((specialist) => (
                                <div className="spec-card" key={specialist.id} hidden={specialist.city !== city}>
                                    <div className="spec-photo">
                                        <img src={specialist.photoUrl} alt={specialist.displayName} loading="lazy" />
                                    </div>
                                    <div className="spec-info">
                                        <div className="spec-name">{specialist.displayName}</div>
                                        {/*
                                         * The card books *this* person: the wizard opens with them
                                         * picked, and the request arrives assigned to them. It is
                                         * still only a starting point — the step can be changed or
                                         * skipped — which is why it is a query parameter rather
                                         * than a route of its own.
                                         */}
                                        <button
                                            type="button"
                                            className="spec-details-btn"
                                            aria-haspopup="dialog"
                                            onClick={() => setDetailsId(specialist.id)}
                                        >
                                            {t('site.spec.details')}
                                        </button>
                                        <Link to={`/${language}/book?specialist=${specialist.id}`} className="spec-btn">
                                            {t('site.spec.btn')}
                                        </Link>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button
                        type="button"
                        className="spec-arrow spec-prev"
                        aria-label={t('site.spec.previous')}
                        onClick={goPrevious}
                    >
                        <svg viewBox="0 0 24 24">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </button>
                    <button
                        type="button"
                        className="spec-arrow spec-next"
                        aria-label={t('site.spec.next')}
                        onClick={goNext}
                    >
                        <svg viewBox="0 0 24 24">
                            <polyline points="9 18 15 12 9 6" />
                        </svg>
                    </button>
                </div>
            </div>
            {details && <SpecialistDetails specialist={details} onClose={() => setDetailsId(null)} />}
        </section>
    );
}
